import React, { forwardRef, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { AsteriskOutlinedIcon, EyeIcon } from '@src/svg';
import InputMask, { ReactInputMask } from 'react-input-mask';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import { ControlledInputPropsType } from '@uikit/FormItems/constants';
import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';
import {
  PasswordStrength,
  ValidStrengthType,
} from '@uikit/FormItems/PasswordStrength';
import { evaluatePasswordStrength, toastError } from '@utils/helpers';
import { useLazyCheckPhoneNumberQuery } from '@store/rtk/twilio';
import { toast } from 'react-toastify';
import { PasswordRequirements } from '@utils/PasswordRequirments';
import { logger } from '@utils/logger';
import { TooltipWrapper } from '@uikit/TooltipWrapper';
import { useTextTruncation } from '@hooks/useTextTruncation';
import { toastMessage } from '@utils/toastMessage';

export const ControlledInput = forwardRef<
  HTMLInputElement,
  ControlledInputPropsType
>(
  (
    {
      name,
      handleKeyDown,
      control,
      rules,
      title,
      placeholder,
      maxWidth,
      disabled,
      type = 'text',
      info,
      IconLeft,
      IconRight = false,
      min,
      max,
      mask,
      onChange,
      onPhoneValidityChange,
      onBlur,
      onFocus,
      onPaste,
      trigger,
      tabIndex,
      addAuthNumberCallback,
      showPasswordStrength = false,
      validateOnSubmitOnly = false,
      hideErrorWhenEmpty = false,
      maxLength,
      uppercase = false,
      loginInputClassname,
      confirmPass,
      forceInvalid,
      dataTestId,
      removePlaceholder,
      required = false,
      optional = false,
    },
    ref
  ) => {
    const inputRef = useRef<ReactInputMask>(null);

    const [typeToggle, setTypeToggle] = useState<boolean>(type !== 'password');
    const [passwordStrength, setPasswordStrength] = useState<
      ValidStrengthType | ''
    >('');
    const [isFocused, setIsFocused] = useState(false);
    const [isPasswordHelpVisible, setIsPasswordHelpVisible] = useState(false);

    const [triggerPhoneLookup] = useLazyCheckPhoneNumberQuery();
    const lastCheckedPhoneRef = useRef<string>('');
    const { tooltipContent, bindTruncation } = useTextTruncation();

    const activePlaceholder = removePlaceholder && isFocused ? '' : placeholder;

    const validatePhoneIfNeeded = async (rawValue: string) => {
      // this should work only for phone numbers
      const isPhoneMask = mask === '(999) 999-9999';
      const digitsOnly = (rawValue || '').replace(/\D/g, '');
      if (!isPhoneMask) return;
      if (digitsOnly.length !== 10) {
        lastCheckedPhoneRef.current = '';
        onPhoneValidityChange?.(false);
        return;
      }
      if (lastCheckedPhoneRef.current === digitsOnly) return;
      lastCheckedPhoneRef.current = digitsOnly;
      onPhoneValidityChange?.(false);
      try {
        const res: any = await triggerPhoneLookup(digitsOnly).unwrap();
        if (lastCheckedPhoneRef.current !== digitsOnly) return;
        const isValid = res?.data?.valid;
        if (isValid === false) {
          toast.error(toastMessage.COMMON.itsNotAValidNumber);
        }
        onPhoneValidityChange?.(isValid !== false);
      } catch (e) {
        if (lastCheckedPhoneRef.current !== digitsOnly) return;
        onPhoneValidityChange?.(false);
        toastError(e, toastMessage.COMMON.failedToValidatePhoneNumber);
        logger.log(e, 'error');
      }
    };

    const handleInternalKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (
        type === 'alphanumeric' &&
        e.key.length === 1 &&
        !/^[a-zA-Z0-9]$/.test(e.key)
      ) {
        e.preventDefault();
      } else if (
        type === 'number' &&
        e.key.length === 1 &&
        !/^[0-9]$/.test(e.key)
      ) {
        e.preventDefault();
      }
      handleKeyDown?.(e);
    };

    return (
      <div style={{ maxWidth }} className="input-text">
        {info?.toggle && (
          <InfoTooltip className="info_shower" title={info?.desc}>
            <div></div>
          </InfoTooltip>
        )}
        <div className={!disabled ? '' : 'disabled'}>
          <div className="input-title-wrapper">
            {title && (
              <span style={{ whiteSpace: 'nowrap' }} className="input-title">
                {title}
                {optional && (
                  <span className="input-optional-label">(optional)</span>
                )}
                {(required || !!rules?.required) && (
                  <span className="input-required-marker" aria-hidden="true">
                    <AsteriskOutlinedIcon
                      width={10}
                      height={10}
                      color="#fb6403"
                    />
                  </span>
                )}
              </span>
            )}
          </div>

          <Controller
            name={name}
            control={control}
            rules={{
              ...rules,
              min: min
                ? { value: min, message: `minimum value is: ${min}` }
                : undefined,
              max: max
                ? { value: max, message: `maximum value is: ${max}` }
                : undefined,
            }}
            render={({ field, fieldState, formState }) => {
              const hasValue = String(field.value ?? '').length > 0;
              const canShowValidation =
                !validateOnSubmitOnly ||
                fieldState.isTouched ||
                fieldState.isDirty ||
                formState.isSubmitted ||
                (hasValue && !!fieldState.error);

              const patternError = fieldState.error?.type === 'pattern';

              const rawInvalid =
                patternError || !!fieldState.error || !!forceInvalid;

              const finalInvalid =
                (!hideErrorWhenEmpty || hasValue) &&
                canShowValidation &&
                rawInvalid;

              const validationDynamicClassName =
                getDynamicValidationColorsGlobal(field, finalInvalid);

              const handleBlur = (
                e: React.FocusEvent<HTMLInputElement | HTMLDivElement>
              ) => {
                if (onBlur) {
                  onBlur(e);
                }
                field.onBlur();
              };

              let value = field.value;
              if (field.value && type === 'duration') {
                const [hours, mins] = field.value.split(':');

                value =
                  field.value && type === 'duration'
                    ? +hours
                      ? `${+hours} hr ${+mins} mins`
                      : `${+mins} mins`
                    : field.value;
              }

              return (
                <TooltipWrapper
                  tooltip={tooltipContent}
                  open={Boolean(tooltipContent)}
                >
                  <span
                    className={`input ${validationDynamicClassName}`}
                    {...bindTruncation}
                  >
                    {IconLeft && type !== 'password' && <IconLeft />}
                    {!mask && (
                      <input
                        className={`truncate-input ${loginInputClassname ? 'loginInputClassname' : ''}`}
                        autoComplete={
                          type === 'password' ? 'new-password' : 'off'
                        }
                        {...field}
                        ref={field.ref}
                        value={value}
                        id={name}
                        disabled={disabled}
                        required={required || undefined}
                        aria-required={required || undefined}
                        onFocus={() => {
                          setIsFocused(true);
                          if (showPasswordStrength && type === 'password') {
                            setIsPasswordHelpVisible(true);
                          }
                          trigger && trigger(name);
                          onFocus && onFocus();
                        }}
                        type={typeToggle ? 'text' : 'password'}
                        placeholder={activePlaceholder}
                        pattern={type === 'number' ? '[0-9]*' : ''}
                        {...(min ? { min: min } : {})}
                        {...(max ? { max: max } : {})}
                        {...(tabIndex ? { tabIndex } : {})}
                        {...(maxLength ? { maxLength } : {})}
                        onKeyDown={handleInternalKeyDown}
                        onPaste={onPaste}
                        onChange={({ target }) => {
                          if (uppercase) {
                            const { selectionStart, selectionEnd } = target;
                            target.value = target.value.toUpperCase();
                            target.setSelectionRange(
                              selectionStart,
                              selectionEnd
                            );
                          }
                          const { value } = target;
                          // no trimming on typing
                          field.onChange(value);
                          if (validateOnSubmitOnly && trigger) {
                            trigger(name);
                          }

                          if (showPasswordStrength && type === 'password') {
                            if (!value) {
                              setPasswordStrength('');
                            } else {
                              setPasswordStrength(
                                evaluatePasswordStrength(value)
                              );
                            }
                          }
                          if (onChange) onChange(name, value);
                        }}
                        onBlur={(e) => {
                          setIsFocused(false);
                          if (typeof e.target.value === 'string') {
                            const trimmed = e.target.value.trim();
                            field.onChange(trimmed);
                            if (showPasswordStrength && type === 'password') {
                              if (!trimmed) {
                                setPasswordStrength('');
                              } else {
                                setPasswordStrength(
                                  evaluatePasswordStrength(trimmed)
                                );
                              }
                            }
                            if (onBlur) onBlur(e);
                          } else {
                            if (onBlur) onBlur(e);
                          }
                          field.onBlur();
                        }}
                        data-test-id={dataTestId}
                      />
                    )}
                    {mask && type !== 'password' && (
                      <InputMask
                        className="truncate-input"
                        autoComplete={'off'}
                        {...field}
                        id={name}
                        mask={mask}
                        placeholder={activePlaceholder}
                        disabled={disabled}
                        required={required || undefined}
                        aria-required={required || undefined}
                        onFocus={() => {
                          trigger && trigger(name);
                        }}
                        ref={inputRef}
                        {...(tabIndex ? { tabIndex } : {})}
                        {...(maxLength ? { maxLength } : {})}
                        onPaste={onPaste}
                        onChange={async (e) => {
                          const value = e.target.value
                            .replace(/[_():\s-]/g, '')
                            .trim();
                          field.onChange({ target: { value } });
                          if (onChange) onChange(name, value);
                          // Trigger phone validation when 10 digits are entered
                          await validatePhoneIfNeeded(value);
                        }}
                        onBlur={(e) => {
                          const value = e.target.value
                            .replace(/[_():\s-]/g, '')
                            .trim();

                          field.onBlur();
                          if (onBlur) onBlur({ target: { value } });
                        }}
                        data-test-id={dataTestId}
                      />
                    )}
                    {type === 'password' && (
                      <button
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setTypeToggle(!typeToggle)}
                      >
                        <EyeIcon toggle={!typeToggle} />
                      </button>
                    )}
                    {addAuthNumberCallback && (
                      <div
                        className="auth-plus-button"
                        onClick={addAuthNumberCallback}
                      >
                        +
                      </div>
                    )}
                  </span>
                  {type === 'password' && showPasswordStrength && (
                    <>
                      <PasswordRequirements
                        value={field.value}
                        visible={isPasswordHelpVisible}
                        confirmValue={confirmPass}
                      />
                      {isPasswordHelpVisible && (
                        <PasswordStrength strength={passwordStrength} />
                      )}
                    </>
                  )}
                </TooltipWrapper>
              );
            }}
          />
        </div>
      </div>
    );
  }
);
